import _ from "lodash";
import { connect } from "react-redux";

import { graphql, compose } from "lib/apolloClient";
import withLoader from "UIComponents/GqlWithLoader";
import DualPanelManageMembers from "../DualPanelManageMembers";
import {
  getGradeUsersQuery,
  getYearGroupUsersQuery,
  getStaffCourseUsersQuery,
  getCalendarEventItemUsersPoolQuery,
  getOrganizationCourseUsersQuery,
} from "MyCalendar/components/EventModalV2/modules/EventModalV2Query";
import {
  SHARE_WITH_RECIPIENT_TYPE,
  mapSharedWithToUserType,
  GROUP_BY_OPTIONS,
  canUserCreateEventForEveryone,
} from "MyCalendar/components/EventModalV2/modules/Utils";
import {
  getOrganizationId,
  getUserId,
  getOrganizationCurriculumPrograms,
} from "modules/CommonSelectors";
import prepareLoadMoreFeed from "Utils/PrepareLoadMoreFeedHOF";
import {
  getCoursesCohorts,
  getYearGroupCohorts,
  getGradesCohorts,
} from "AppComponents/DualPanelManageMembers/module/utils";
import { getIsAdminByRole } from "lib/staffPermissionUtils";
import {
  getUserEventCreationAccessLevel,
  getSelectedAcademicYearIdForSchool,
  getSelectedSchoolDetailsForCalendar,
} from "MyCalendar/modules/Utils";
import ACLStorage from "lib/aclService";
import ACLStore from "lib/aclStore";
import {
  CAN_MANAGE_CALENDAR_EVENTS_AND_MEETINGS_PERMISSION,
  ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION,
  ASSIGNMENTS_MANAGE_PERMISSION_IN_CALENDAR,
} from "Constants/permissionConstants";

const mapStateToProps = state => {
  const {
    currentCurriculumProgramId: selectedCurriculumId,
  } = getSelectedSchoolDetailsForCalendar(state);

  const userEventCreationAccessLevel = getUserEventCreationAccessLevel({
    currentCurriculumProgramId: selectedCurriculumId,
    alsoCheckClassLevelPermission: false,
  });
  const userCanCreateHolidaysAndWholeSchoolEvents = _.includes(
    ["ACCOUNT_LEVEL", "PROGRAM_LEVEL"],
    userEventCreationAccessLevel
  );
  const userHasAccoutLevelAccess = _.includes(
    ["ACCOUNT_LEVEL"],
    userEventCreationAccessLevel
  );

  const organizationCurriculumPrograms = getOrganizationCurriculumPrograms(
    state
  );
  const canPublishToAll = canUserCreateEventForEveryone({
    currentCurriculumProgramId: selectedCurriculumId,
  });

  const programsUserCanPublishTo = _.filter(
    organizationCurriculumPrograms,
    ({ id }) =>
      ACLStorage.getPermissionValueByContext(
        CAN_MANAGE_CALENDAR_EVENTS_AND_MEETINGS_PERMISSION,
        [
          {
            entityId: id,
            entityType: "CURRICULUM_PROGRAM",
          },
        ]
      )
  );
  return {
    selectedCurriculumId,
    academicYearId: getSelectedAcademicYearIdForSchool(state),
    isUserAdminByRole: getIsAdminByRole(),
    userCanCreateHolidaysAndWholeSchoolEvents,
    userHasAccoutLevelAccess,
    calendarEventItemType: _.get(
      state,
      "myCalendar.eventModal.calendarEventItemType"
    ),
    queryFilter: canPublishToAll
      ? undefined
      : {
          curriculumProgramIds: _.map(programsUserCanPublishTo, ({ id }) => id),
        },
  };
};

const EMPTY_ARRAY = [];

const getSkipSharedWithVariables = sharedWith => ({
  skipStudents: !_.includes(sharedWith, SHARE_WITH_RECIPIENT_TYPE.STUDENT),
  skipStaff: !_.includes(sharedWith, SHARE_WITH_RECIPIENT_TYPE.STAFF),
  skipFamilyMembers: !_.includes(
    sharedWith,
    SHARE_WITH_RECIPIENT_TYPE.FAMILY_MEMBER
  ),
});

const QueryWrapper = compose(
  connect(mapStateToProps),
  graphql(getCalendarEventItemUsersPoolQuery, {
    name: "getAnnouncementUsersPool",
    skip: ({ typeFilter }) => typeFilter !== GROUP_BY_OPTIONS.INDIVIDUAL,
    options: ({
      academicYearId,
      selectedCurriculumId,
      sharedWith,
      searchText,
      curriculumProgramIds,
      calendarEventItemType,
    }) => ({
      fetchPolicy: "cache-and-network",
      context: { useHttpLink: true },
      variables: {
        organizationId: getOrganizationId(),
        // Basically only fetch nested family members of a student,
        // if familyMember is selected in sharedWith
        getStudentFamilyMembers: _.includes(
          sharedWith,
          SHARE_WITH_RECIPIENT_TYPE.FAMILY_MEMBER
        ),
        getOnlyUserId: false,
        first: 30,
        filters: {
          userStatus: "ACTIVE",
          userType: _.map(
            sharedWith,
            sharedType => mapSharedWithToUserType[sharedType]
          ),
          searchText: _.trim(searchText) || undefined,
          academicYearIds: academicYearId ? [academicYearId] : undefined,
          curriculumProgramIds,
          ...(calendarEventItemType
            ? { eventType: calendarEventItemType }
            : {}),
        },
      },
    }),
    props: ({ getAnnouncementUsersPool }) => {
      const { node, networkStatus, variables } = getAnnouncementUsersPool;
      const { hasNextPage = false, endCursor } = _.get(
        node,
        "calendarEventItemUsers.pageInfo",
        {}
      );
      const userNodeList = _.get(node, "calendarEventItemUsers.edges", []);

      const membersParams = {
        userNodeList,
        hasNextPage,
        networkStatus,
        isFetchingMore: networkStatus === 3,

        loadMoreFeed: prepareLoadMoreFeed({
          variables: {
            ...variables,
            before: endCursor,
          },
          queryData: getAnnouncementUsersPool,
          key: "calendarEventItemUsers",
        }),
      };

      return {
        isData: !_.isEmpty(node),
        isLoading: _.includes([1, 2], networkStatus),
        membersParams,
      };
    },
  }),
  graphql(getOrganizationCourseUsersQuery, {
    name: "getOrganizationCourseUsers",
    skip: ({ userCanCreateHolidaysAndWholeSchoolEvents, typeFilter }) =>
      typeFilter !== GROUP_BY_OPTIONS.COURSE ||
      !userCanCreateHolidaysAndWholeSchoolEvents,
    options: props => {
      const {
        selectedCurriculumId,
        searchText,
        sharedWith,
        academicYearId,
        userHasAccoutLevelAccess,
        curriculumProgramIds,
        getOnlyUserId,
        getCurriculumProgram,
      } = props;

      return {
        fetchPolicy: "cache-and-network",
        context: { useHttpLink: true },
        variables: {
          organizationId: getOrganizationId(),
          first: 10,
          ...getSkipSharedWithVariables(sharedWith),
          getOnlyUserId: getOnlyUserId ?? false,
          getCurriculumProgram: getCurriculumProgram ?? false,
          filters: {
            searchText,
            academicYears: [academicYearId],
            ...(!curriculumProgramIds && !userHasAccoutLevelAccess
              ? { curriculumProgramIds: [selectedCurriculumId] }
              : {}),
            ...(curriculumProgramIds ? { curriculumProgramIds } : {}),
          },
          curriculumProgramIds: undefined,
        },
      };
    },
    props: ({ getOrganizationCourseUsers }) => {
      const {
        variables: queryVariables,
        node,
        networkStatus,
      } = getOrganizationCourseUsers;

      const { hasNextPage = false, endCursor } = _.get(
        node,
        "courses.pageInfo",
        {}
      );
      const courses = _.get(node, "courses.edges", EMPTY_ARRAY);
      const coursesCohorts = _.map(getCoursesCohorts(courses), cohort => ({
        ...cohort,
        curriculumProgramLabel: _.get(
          _.find(courses, c => _.get(c, "node.id") === cohort.id),
          "node.curriculumProgram.label"
        ),
      }));

      const courseUserParams = {
        cohorts: coursesCohorts,
        hasNextPage,
        networkStatus,
        loadMoreFeed: prepareLoadMoreFeed({
          variables: {
            ...queryVariables,
            after: endCursor,
          },
          queryData: getOrganizationCourseUsers,
          key: "courses",
        }),
      };
      return {
        isData: !_.isEmpty(node),
        isLoading: _.includes([1, 2], networkStatus),
        courseUserParams,
      };
    },
  }),
  graphql(getStaffCourseUsersQuery, {
    name: "getCourseUsers",
    skip: ({ userCanCreateHolidaysAndWholeSchoolEvents, typeFilter }) =>
      typeFilter !== GROUP_BY_OPTIONS.COURSE ||
      userCanCreateHolidaysAndWholeSchoolEvents,
    options: props => {
      const {
        searchText,
        academicYearId,
        selectedCurriculumId,
        sharedWith,
        curriculumProgramIds,
        getOnlyUserId,
        getCurriculumProgram,
      } = props;

      return {
        fetchPolicy: "cache-and-network",
        context: { useHttpLink: true },
        variables: {
          first: 10,
          id: getUserId(),
          organizationId: getOrganizationId(),
          ...getSkipSharedWithVariables(sharedWith),
          academicYearIds: academicYearId ? [academicYearId] : undefined,
          curriculumProgramIds: undefined,
          getOnlyUserId: getOnlyUserId ?? false,
          getCurriculumProgram: getCurriculumProgram ?? false,
          filters: {
            searchText: _.trim(searchText) || undefined,
            fetchOnlyMappedCourses: false,
            academicYears: [academicYearId],
            curriculumProgramIds: curriculumProgramIds ?? [
              selectedCurriculumId,
            ],
            ...(ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)
              ? {
                  controlParameters: [
                    ASSIGNMENTS_MANAGE_PERMISSION_IN_CALENDAR,
                  ],
                }
              : {}),
          },
        },
      };
    },
    props: ({ getCourseUsers }) => {
      const { variables: queryVariables, node, networkStatus } = getCourseUsers;

      const { hasNextPage = false, endCursor } = _.get(
        node,
        "coursesV2.pageInfo",
        {}
      );

      const courses = _.get(node, "coursesV2.edges", EMPTY_ARRAY);
      const coursesCohorts = _.map(getCoursesCohorts(courses), cohort => ({
        ...cohort,
        curriculumProgramLabel: _.get(
          _.find(courses, c => _.get(c, "node.id") === cohort.id),
          "node.curriculumProgram.label"
        ),
      }));

      const courseUserParams = {
        cohorts: coursesCohorts,
        hasNextPage,
        networkStatus,
        loadMoreFeed: prepareLoadMoreFeed({
          variables: {
            ...queryVariables,
            after: endCursor,
          },
          queryData: getCourseUsers,
          key: "coursesV2",
        }),
      };

      return {
        isData: !_.isEmpty(node),
        isLoading: _.includes([1, 2], networkStatus),
        courseUserParams,
      };
    },
  }),
  graphql(getYearGroupUsersQuery, {
    name: "getYearGroupUsers",
    skip: ({ typeFilter }) => typeFilter !== GROUP_BY_OPTIONS.YEAR_GROUP,
    options: props => {
      const {
        searchText,
        selectedCurriculumId,
        sharedWith,
        getOnlyUserId,
        getCurriculumProgram,
      } = props;

      return {
        fetchPolicy: "cache-and-network",
        context: { useHttpLink: true },
        variables: {
          organizationId: getOrganizationId(),
          ...getSkipSharedWithVariables(sharedWith),
          getOnlyUserId: getOnlyUserId ?? false,
          getCurriculumProgram: getCurriculumProgram ?? false,
          filters: {
            searchText: _.trim(searchText) || undefined,
          },
          curriculumProgramIds: undefined,
        },
      };
    },
    props: ({ getYearGroupUsers }) => {
      const { node, networkStatus } = getYearGroupUsers;

      const yearGroups = _.get(node, "yearGroups", EMPTY_ARRAY);

      const yearGroupsCohorts = _.map(
        getYearGroupCohorts(yearGroups),
        cohort => ({
          ...cohort,
          curriculumProgramLabel: _.get(
            _.find(yearGroups, yg => yg.id === cohort.id),
            "curriculumProgram.label"
          ),
        })
      );
      const yearGroupUserParams = {
        cohorts: yearGroupsCohorts,
        hasNextPage: false,
        networkStatus: getYearGroupUsers["networkStatus"],
        loadMoreFeed: _.noop,
      };

      return {
        isData: !_.isEmpty(node),
        isLoading: _.includes([1, 2], networkStatus),
        yearGroupUserParams,
      };
    },
  }),
  graphql(getGradeUsersQuery, {
    name: "getGradeUsers",
    skip: ({ typeFilter }) => typeFilter !== GROUP_BY_OPTIONS.GRADE,
    options: props => {
      const {
        searchText,
        selectedCurriculumId,
        sharedWith,
        queryFilter,
        curriculumProgramIds,
        getOnlyUserId,
        getCurriculumProgram,
      } = props;

      return {
        fetchPolicy: "cache-and-network",
        context: { useHttpLink: true },
        variables: {
          organizationId: getOrganizationId(),
          ...getSkipSharedWithVariables(sharedWith),
          getOnlyUserId: getOnlyUserId ?? false,
          getCurriculumProgram: getCurriculumProgram ?? false,
          filters: {
            searchText: _.trim(searchText) || undefined,
            ...queryFilter,
            ...(curriculumProgramIds ? { curriculumProgramIds } : {}),
          },
          curriculumProgramIds: undefined,
        },
      };
    },
    props: ({ getGradeUsers }) => {
      const { node, networkStatus, variables: queryVariables } = getGradeUsers;

      const grades = _.get(node, "grades", EMPTY_ARRAY);
      const gradesCohorts = _.map(getGradesCohorts(grades), cohort => ({
        ...cohort,
        curriculumProgramLabel: _.get(
          _.find(grades, g => g.id === cohort.id),
          "curriculumProgram.label"
        ),
      }));
      const gradeUserParams = {
        cohorts: gradesCohorts,
        hasNextPage: _.get(node, "grades.pageInfo.hasNextPage", false),
        networkStatus,
        loadMoreFeed: prepareLoadMoreFeed({
          variables: {
            ...queryVariables,
            after: _.get(node, "grades.pageInfo.endCursor"),
          },
          queryData: getGradeUsers,
          key: "courses",
        }),
      };

      return {
        isData: !_.isEmpty(node),
        isLoading: _.includes([1, 2], networkStatus),
        gradeUserParams,
      };
    },
  }),

  withLoader
)(DualPanelManageMembers.CohortList);

export const metadataProps = {
  emptyStateSubtitle: "common:add_members_v3_search_empty_state_subtitle",
  countUsersSelectedText: "announcement:user_selected_with_count",
  selectedUsersListText: "calendar:selected_recipients",
  disabledUserTooltipText: "announcement:user_already_added_as_a_recipient",
  disabledNestedFamilyTooltipText:
    "announcement:user_already_added_as_a_recipient",
  filterLabelText: {
    label: "common:group_by",
  },
  filterLabelDefaultValue: GROUP_BY_OPTIONS.INDIVIDUAL,
};

export default QueryWrapper;
